"use client";

import React, { useState, useRef } from "react";
import { ShieldCheck, Lock, ArrowRight, Upload, X, FileText, Image, Video, FileUp, CircleAlert, Youtube, Instagram, Facebook, Globe, ExternalLink, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
export default function Mediauploadsection() {
  const [saveCard, setSaveCard] = useState<boolean>(true);
  const [sourceAnonimity, setSourceAnonimity] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<{
    id: string;
    name: string;
    url: string;
  }[]>([]);
  const [isPortrait, setIsPortrait] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const validateAndAddFile = (file: File) => {
    if (file.type.startsWith('image/')) {
      if (file.size > 10485760) {
        alert('Ukuran foto terlalu besar, maksimal 10MB');
        return;
      }
      simulateUpload(file);
    } else {
      simulateUpload(file);
    }
  };
  const simulateUpload = (file: File) => {
    setIsUploading(true);
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsUploading(false);
          const newFile = {
            id: Math.random().toString(36).substr(2, 9),
            name: file.name,
            url: URL.createObjectURL(file)
          };
          setFiles(prevFiles => [...prevFiles, newFile]);
          return 100;
        }
        return prev + 10;
      });
    }, 100);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      validateAndAddFile(droppedFiles[0]);
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) {
      validateAndAddFile(selectedFiles[0]);
    }
  };
  const removeFile = (id: string) => {
    setFiles(files.filter(f => f.id !== id));
  };
  const supportLinks = [{
    name: "YouTube",
    icon: <Youtube className="size-4" />,
    url: "https://youtube.com"
  }, {
    name: "TikTok",
    icon: <ExternalLink className="size-4" />,
    url: "https://tiktok.com"
  }, {
    name: "Instagram",
    icon: <Instagram className="size-4" />,
    url: "https://instagram.com"
  }, {
    name: "Facebook",
    icon: <Facebook className="size-4" />,
    url: "https://facebook.com"
  }, {
    name: "Google Drive",
    icon: <FileUp className="size-4" />,
    url: "https://drive.google.com"
  }, {
    name: "Website",
    icon: <Globe className="size-4" />,
    url: "https://abadi.news"
  }];
  return <section data-section-id="253511" id="mediauploadsection" className="bg-background text-foreground py-20 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] rounded-lg bg-primary/3 blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] rounded-lg bg-secondary/3 blur-[120px]" />
      </div>

      <div ref={ref} className={cn("container mx-auto px-4 md:px-6 relative z-10 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
        <div className="mb-12 md:mb-20 max-w-3xl">
          <Badge variant="outline" className="mb-4 border-primary/20 text-primary px-3 py-1 rounded-lg">
            Kontribusi Publik
          </Badge>
          <h2 className="font-default text-4xl md:text-5xl font-bold text-foreground mb-6 tracking-tight">
            Kirim Materi Berita & Laporan
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl font-default leading-relaxed max-w-2xl">
            Lengkapi laporan Anda dengan bukti visual yang kuat. Kami menjamin kerahasiaan sumber informasi Anda melalui sistem enkripsi tingkat tinggi.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {}
          <div className="lg:col-span-7 flex flex-col gap-8">
            {isPortrait && <Alert className="bg-destructive/5 border-destructive/20 rounded-lg">
                <CircleAlert className="size-4 text-destructive" />
                <AlertTitle className="text-destructive font-bold">Peringatan Format</AlertTitle>
                <AlertDescription className="text-destructive/80">
                  Foto harus landscape untuk hasil terbaik di portal berita. Silakan potong atau ganti foto.
                </AlertDescription>
              </Alert>}

            <div className="w-full group">
              <AspectRatio ratio={16 / 9} className="w-full">
                <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} className={cn("relative h-full w-full rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-6 cursor-pointer transition-all duration-500 p-8 overflow-hidden", isDragging ? "border-primary bg-primary/5 scale-[1.01]" : "border-border bg-muted/30 hover:bg-muted/50 hover:border-primary/40")}>
                  <div className={cn("p-6 rounded-lg transition-all duration-500 shadow-sm", isDragging ? "bg-primary text-primary-foreground scale-110" : "bg-background text-muted-foreground group-hover:text-primary")}>
                    <Upload className="size-10" />
                  </div>
                  <div className="text-center space-y-2">
                    <p className="font-default font-bold text-xl text-foreground">
                      Klik atau seret foto utama ke sini
                    </p>
                    <p className="text-sm text-muted-foreground font-default max-w-xs mx-auto">
                      Mendukung format JPG, JPEG, PNG hingga 10MB. Pastikan gambar memiliki pencahayaan yang baik.
                    </p>
                  </div>
                  
                  {isUploading && <div className="absolute bottom-0 left-0 w-full p-6 bg-background/80 backdrop-blur-sm">
                      <div className="flex justify-between text-xs font-medium mb-2">
                        <span>Mengunggah...</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <Progress value={uploadProgress} className="h-1.5 bg-muted" />
                    </div>}

                  <input ref={fileInputRef} type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                </div>
              </AspectRatio>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div onClick={() => videoInputRef.current?.click()} className="p-6 border border-border rounded-lg bg-card flex items-center gap-4 cursor-pointer hover:border-primary/50 hover:shadow-md transition-all duration-300 group">
                <div className="p-3 bg-primary/10 text-primary rounded-lg group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <Video className="size-6" />
                </div>
                <div>
                  <p className="text-base font-bold">Unggah Video</p>
                  <p className="text-xs text-muted-foreground">MP4, MOV (Maks 100MB)</p>
                </div>
                <input ref={videoInputRef} type="file" className="hidden" accept="video/*" onChange={handleFileChange} />
              </div>
              <div onClick={() => docInputRef.current?.click()} className="p-6 border border-border rounded-lg bg-card flex items-center gap-4 cursor-pointer hover:border-secondary/50 hover:shadow-md transition-all duration-300 group">
                <div className="p-3 bg-secondary/10 text-secondary rounded-lg group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors">
                  <FileText className="size-6" />
                </div>
                <div>
                  <p className="text-base font-bold">Unggah Dokumen</p>
                  <p className="text-xs text-muted-foreground">PDF, DOCX (Maks 25MB)</p>
                </div>
                <input ref={docInputRef} type="file" className="hidden" accept=".pdf,.docx,.doc" onChange={handleFileChange} />
              </div>
            </div>

            <div className="pt-4">
              <div className="flex items-center gap-3 mb-6">
                <Separator className="flex-1" />
                <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.2em] whitespace-nowrap">Tautan Pendukung</h4>
                <Separator className="flex-1" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {supportLinks.map((link, i) => <Link key={i} data-index={i} to={link.url} newTab className="flex items-center gap-3 p-3.5 rounded-lg border border-border bg-card hover:bg-muted hover:border-primary/30 transition-all duration-300 text-sm font-medium text-muted-foreground hover:text-foreground shadow-sm">
                    <span className="text-primary/70">{link.icon}</span>
                    <span>{link.name}</span>
                  </Link>)}
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <Card className="bg-card border-border shadow-xl overflow-hidden rounded-lg">
              <div className="h-1.5 w-full bg-gradient-to-r from-primary via-primary/80 to-secondary" />
              <CardHeader className="pb-6 pt-8">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="font-default text-2xl font-bold">Pratinjau Unggahan</CardTitle>
                    <CardDescription className="mt-1.5 font-default text-base">Materi yang akan dikirim ke redaksi</CardDescription>
                  </div>
                  <Badge variant="secondary" className="gap-1.5 py-1.5 px-3 bg-primary/10 text-primary border-none font-bold">
                    <ShieldCheck className="size-4" /> 
                    Secure
                  </Badge>
                </div>
              </CardHeader>
              
              <CardContent className="grid gap-8 p-6">
                <div className="min-h-[160px] border border-border rounded-lg bg-muted/20 p-5 relative">
                  {files.length > 0 ? <div className="grid grid-cols-3 gap-4">
                      {files.map((file, i) => <div key={file.id} data-index={i} className="relative aspect-square rounded-lg overflow-hidden border border-border group shadow-sm">
                          {file.name.match(/\.(jpg|jpeg|png|gif)$/i) ? <img src={file.url} alt={file.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" /> : <div className="w-full h-full flex flex-col items-center justify-center bg-muted gap-2">
                              <FileText className="size-8 text-muted-foreground" />
                              <span className="text-[10px] px-2 text-center truncate w-full text-muted-foreground">{file.name}</span>
                            </div>}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button variant="destructive" size="icon" className="size-8 rounded-lg" onClick={() => removeFile(file.id)}>
                              <X className="size-4" />
                            </Button>
                          </div>
                        </div>)}
                    </div> : <div className="h-full flex flex-col items-center justify-center text-center py-8 space-y-3">
                      <div className="p-3 bg-muted rounded-lg">
                        <Image className="size-6 text-muted-foreground/50" />
                      </div>
                      <p className="text-sm text-muted-foreground font-default italic">Belum ada file yang dipilih untuk diunggah</p>
                    </div>}
                </div>

                <div className="bg-muted/30 rounded-lg p-6 space-y-6 border border-border/50">
                  <div className="flex items-center justify-between gap-6">
                    <Label htmlFor="save-card" className="flex flex-col gap-1.5 cursor-pointer flex-1">
                      <span className="text-foreground font-bold text-base font-default">Sertakan Watermark</span>
                      <span className="font-normal text-xs text-muted-foreground font-default leading-relaxed">Lindungi hak cipta karya Anda dengan tanda air resmi ABADI.NEWS</span>
                    </Label>
                    <Switch id="save-card" checked={saveCard} onCheckedChange={setSaveCard} className="data-[state=checked]:bg-primary" />
                  </div>
                  <Separator className="bg-border/50" />
                  <div className="flex items-center justify-between gap-6">
                    <Label htmlFor="source-anonimity" className="flex flex-col gap-1.5 cursor-pointer flex-1">
                      <span className="text-foreground font-bold text-base font-default">Anonimitas Sumber</span>
                      <span className="font-normal text-xs text-muted-foreground font-default leading-relaxed">Identitas Anda akan disembunyikan sepenuhnya dari publik dan pihak ketiga</span>
                    </Label>
                    <Switch id="source-anonimity" checked={sourceAnonimity} onCheckedChange={setSourceAnonimity} className="data-[state=checked]:bg-primary" />
                  </div>
                </div>

                <div className="space-y-4">
                  
                  <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                    <Lock className="size-3" />
                    <span>Enkripsi SSL 256-bit Aktif</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="mt-8 p-6 bg-primary/5 rounded-lg border border-primary/10 flex items-start gap-4">
              <div className="p-2 bg-primary/10 rounded-lg text-primary shrink-0">
                <CheckCircle2 className="size-5" />
              </div>
              <div>
                <h5 className="font-bold text-sm mb-1">Verifikasi Cepat</h5>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Tim redaksi kami akan memverifikasi materi Anda dalam waktu kurang dari 24 jam sebelum dipublikasikan.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}